import React, { createContext, useState, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { createTheme, ThemeProvider as MUIThemeProvider } from "@material-ui/core/styles";
import { CssBaseline } from "@material-ui/core";

const ThemeContext = createContext();

const THEME_PRESETS = [
  {
    id: "azul",
    name: "Órbita Lavanda",
    description: "Lavanda profesional con navegación nocturna.",
    primary: "#6c55f5",
    secondary: "#ef5b67",
    darkSurface: "#21183b",
    darkBackground: "#130c27",
    lightBackground: "#f7f7fb",
  },
  {
    id: "bosque",
    name: "Verde Bosque",
    description: "Verde sobrio para una experiencia relajada.",
    primary: "#2ea86f",
    secondary: "#d5a332",
    darkSurface: "#203936",
    darkBackground: "#152622",
    lightBackground: "#f1faf4",
  },
  {
    id: "oceano",
    name: "Verde Océano",
    description: "Tonos de agua frescos y modernos.",
    primary: "#16b6d3",
    secondary: "#b978c5",
    darkSurface: "#173e4b",
    darkBackground: "#102933",
    lightBackground: "#effbfe",
  },
  {
    id: "atardecer",
    name: "Naranja Atardecer",
    description: "Colores cálidos y fáciles de distinguir.",
    primary: "#db9a22",
    secondary: "#9166ca",
    darkSurface: "#3a3028",
    darkBackground: "#251f1b",
    lightBackground: "#fff8ef",
  },
  {
    id: "violeta",
    name: "Violeta Noche",
    description: "Violeta intenso para equipos creativos.",
    primary: "#8757d6",
    secondary: "#e378b0",
    darkSurface: "#2b2544",
    darkBackground: "#1c1830",
    lightBackground: "#f8f5ff",
  },
  {
    id: "rubor",
    name: "Rosa Rubor",
    description: "Rosa moderno con alto contraste.",
    primary: "#d95088",
    secondary: "#6b9fe8",
    darkSurface: "#402739",
    darkBackground: "#291923",
    lightBackground: "#fff5f9",
  },
];

const readPreference = (key, fallback) => {
  if (typeof window === "undefined") return fallback;
  const saved = window.localStorage.getItem(key);
  return saved === null ? fallback : saved;
};

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(
    () => readPreference("crm-dark-mode", "false") === "true"
  );
  const [themeId, setThemeId] = useState(() =>
    readPreference("crm-theme-id", THEME_PRESETS[0].id)
  );
  const activeTheme =
    THEME_PRESETS.find((preset) => preset.id === themeId) || THEME_PRESETS[0];

  const toggleTheme = () => {
    setDarkMode((prevMode) => {
      const nextMode = !prevMode;
      if (typeof window !== "undefined") {
        window.localStorage.setItem("crm-dark-mode", String(nextMode));
      }
      return nextMode;
    });
  };

  const setThemePreset = (id) => {
    setThemeId(id);
    if (typeof window !== "undefined") {
      window.localStorage.setItem("crm-theme-id", id);
    }
  };

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif',
          h1: { fontWeight: 800, letterSpacing: "-.035em" },
          h2: { fontWeight: 800, letterSpacing: "-.03em" },
          h3: { fontWeight: 780, letterSpacing: "-.025em" },
          h4: { fontWeight: 760, letterSpacing: "-.02em" },
          h5: { fontWeight: 740, letterSpacing: "-.018em" },
          h6: { fontWeight: 720, letterSpacing: "-.012em" },
          button: { fontWeight: 750, textTransform: "none", letterSpacing: 0 },
        },
        shape: { borderRadius: 14 },
        palette: {
          type: darkMode ? "dark" : "light",
          primary: { main: activeTheme.primary },
          secondary: { main: activeTheme.secondary },
          background: darkMode
            ? { default: activeTheme.darkBackground, paper: activeTheme.darkSurface }
            : { default: activeTheme.lightBackground, paper: "#ffffff" },
          text: darkMode
            ? { primary: "#f8fafc", secondary: "#cbd5e1" }
            : { primary: "#172033", secondary: "#5b6475" },
        },
        scrollbarStyles: {
          "&::-webkit-scrollbar": { width: 7, height: 7 },
          "&::-webkit-scrollbar-track": { background: "transparent" },
          "&::-webkit-scrollbar-thumb": {
            borderRadius: 10,
            backgroundColor: darkMode ? "rgba(255,255,255,.18)" : "rgba(53,42,91,.18)",
          },
        },
        overrides: {
          MuiCssBaseline: {
            "@global": {
              "*": { boxSizing: "border-box" },
              body: { letterSpacing: "-.006em" },
              button: { fontFamily: 'Inter, "Segoe UI", Roboto, Arial, sans-serif' },
            },
          },
          MuiPaper: {
            rounded: { borderRadius: 14 },
            outlined: { borderColor: darkMode ? "rgba(255,255,255,.1)" : "#e7e4ef" },
          },
          MuiButton: {
            root: { borderRadius: 11, minHeight: 38, paddingLeft: 16, paddingRight: 16 },
            contained: { boxShadow: "0 7px 18px rgba(79,55,190,.16)" },
          },
          MuiIconButton: {
            root: { borderRadius: 11, transition: "background-color 150ms ease, transform 150ms ease" },
          },
          MuiTab: {
            root: { textTransform: "none", fontWeight: 700 },
          },
          MuiTabs: {
            indicator: { height: 3, borderRadius: "3px 3px 0 0" },
          },
          MuiOutlinedInput: {
            root: {
              borderRadius: 12,
              "&:hover $notchedOutline": { borderColor: activeTheme.primary },
            },
          },
          MuiTooltip: {
            tooltip: { borderRadius: 9, padding: "7px 10px", fontSize: ".76rem", fontWeight: 650 },
          },
        },
      }),
    [darkMode, activeTheme]
  );

  const contextValue = useMemo(
    () => ({
      darkMode,
      toggleTheme,
      activeTheme,
      setThemePreset,
      themePresets: THEME_PRESETS,
    }),
    [darkMode, activeTheme]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <MUIThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};
ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useThemeContext = () => useContext(ThemeContext);
